import { useCallback, useEffect, useMemo, useState } from "react";
import {
  PendingActionData,
  upsertStagedTagEdit,
  removeStagedTagEdit,
  upsertStagedRuleEdit,
  removeStagedRuleEdit,
} from "../../../redux/feature/chat/askai-chat-slice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/hooks";
import { CoObjectItem, CoObjectSummary } from "../../interfaces/CoObject";
import { Rule, RuleType } from "../../interfaces/Rule";
import { Tag } from "../../interfaces/Tag";
import { CoObjectTypeEnum } from "../../../api/models";
import { formatMessageDefault, formatMessageWithParamsDefault } from "../../../utils/helpers/LanguageHelper";
import { validateRule } from "../../../utils/helpers/RulesHelper";
import { TagScriptToken, tokenizeScriptForRendering } from "../../../utils/grammar/RuleGrammarParser";
import { selectCellOrContentControl } from "../../../office/ContentControl";
import { renderCellTitle } from "../../../utils/helpers/TagsHelper";
import { CAPTION_REVIEW_MODAL_OUTDATED_TAG_EDIT, CAPTION_REVIEW_MODAL_OUTDATED_RULE_EDIT } from "./validationCaptions";

const TAGS_SECTION_ID = "tags-section";
const REVIEW_TAG_ID_PREFIX = "review-ai-tag";
const EDIT_TAG_DRAFT_PREFIX = "edit-tag-";
const EDIT_RULE_DRAFT_PREFIX = "edit-rule-";

export type AskAiReviewObjectDraft = CoObjectSummary;

export interface AskAiReviewTagDraft extends Omit<Tag, "objects"> {
  draftKey: string;
  objects: AskAiReviewObjectDraft[];
  selectedObjectIds: string[];
  /** Discriminator for unified list: "new" = proposed via propose_tags; "edit" = staged via edit_tag. */
  origin: "new" | "edit";
  /** Only set when origin === "edit". The committed tag id being edited. */
  targetId?: string;
}

export interface AskAiReviewRuleDraft extends Rule {
  draftKey: string;
  /** Discriminator for unified list: "new" = proposed via propose_rules; "edit" = staged via edit_rule. */
  origin: "new" | "edit";
  /** Only set when origin === "edit". The committed rule id being edited. */
  targetId?: string;
}

export interface AskAiReviewTagObjectDisplayItem {
  kind: "object";
  object: AskAiReviewObjectDraft;
  objectItem: CoObjectItem;
}

export interface AskAiReviewTagTableCellDisplayItem {
  object: AskAiReviewObjectDraft;
  objectItem: CoObjectItem;
}

export interface AskAiReviewTagTableDisplayItem {
  kind: "table";
  groupId: string;
  tableObjectItem: CoObjectItem;
  cells: AskAiReviewTagTableCellDisplayItem[];
}

export type AskAiReviewTagDisplayItem = AskAiReviewTagObjectDisplayItem | AskAiReviewTagTableDisplayItem;

export interface AskAiReviewSaveModalProps {
  isShown: boolean;
  onHide: () => void;
  onSave: (reviewedData: PendingActionData) => void;
  documentId?: string;
  coObjects?: CoObjectItem[];
  rules?: Rule[];
  tags?: Tag[];
  existingRules?: Rule[];
  availableTags?: Tag[];
}

export interface RuleDraftErrors {
  titleError: string;
  scriptError: string;
  consistencyError: string;
}

export interface TagDraftErrors {
  labelError: string;
  objectsError: string;
}

export interface AskAiReviewSaveModalResult {
  ruleDrafts: AskAiReviewRuleDraft[];
  tagDrafts: AskAiReviewTagDraft[];
  reviewTags: Tag[];
  referenceTags: Tag[];
  expandedRuleIds: string[];
  expandedTagSectionIds: string[];
  expandedTagObjectIds: string[];
  expandedTableGroupIds: string[];
  handleRulesAccordionToggle: (_event: unknown, data: { openItems?: readonly unknown[] }) => void;
  handleTagsAccordionToggle: (_event: unknown, data: { openItems?: readonly unknown[] }) => void;
  handleTagObjectsAccordionToggle: (_event: unknown, data: { openItems?: readonly unknown[] }) => void;
  handleTableGroupAccordionToggle: (groupId: string, _event: unknown, data: { openItems?: readonly unknown[] }) => void;
  handleRuleTitleChange: (ruleDraftKey: string, nextTitle: string) => void;
  handleRuleScriptChange: (ruleDraftKey: string, nextScript: string) => void;
  handleRuleTagIdsChange: (ruleDraftKey: string, nextTagIds: string[]) => void;
  handleRuleDelete: (ruleDraftKey: string) => void;
  handleTagLabelChange: (tagDraftKey: string, nextLabel: string) => void;
  handleTagObjectToggle: (tagDraftKey: string, objectId: string, selected?: boolean) => void;
  handleObjectClick: (object: CoObjectSummary) => void;
  handleCancel: () => void;
  handleSave: () => void;
  getRuleStatusLabel: (ruleId: string) => string;
  getTaggedItemsLabel: (count: number) => string;
  getRuleDraftErrors: (ruleDraftKey: string) => RuleDraftErrors;
  getTagDraftErrors: (tagDraftKey: string) => TagDraftErrors;
  isSaveEnabled: boolean;
  getObjectItem: (object: CoObjectSummary) => CoObjectItem;
  getTagDisplayItems: (tagDraft: AskAiReviewTagDraft) => AskAiReviewTagDisplayItem[];
  /** Internal edit-draft state — kept for hook-level tests. View code must NOT branch on these for rendering. */
  tagEditDrafts: Record<string, Tag>;
  ruleEditDrafts: Record<string, Rule>;
  outdatedTagEditIds: string[];
  outdatedRuleEditIds: string[];
  updateTagEditDraft: (targetId: string, patch: Partial<Tag>) => void;
  removeTagEditDraft: (targetId: string) => void;
  updateRuleEditDraft: (targetId: string, patch: Partial<Rule>) => void;
  removeRuleEditDraft: (targetId: string) => void;
  discardOutdatedTagEdit: (targetId: string) => void;
  discardOutdatedRuleEdit: (targetId: string) => void;
  hasPendingEdits: boolean;
}

function normalizeOpenItems(data: { openItems?: readonly unknown[] }): string[] {
  return Array.isArray(data.openItems) ? data.openItems.filter((item): item is string => typeof item === "string") : [];
}

function toggleInArray(items: string[], item: string, include: boolean): string[] {
  if (include) {
    return Array.from(new Set([...items, item]));
  }
  return items.filter((i) => i !== item);
}

function getObjectIdAsString(objectId: unknown): string | undefined {
  return typeof objectId === "string" && objectId.trim() ? objectId : undefined;
}

function parseCellAddress(object: CoObjectSummary): { row: number; column: number } | null {
  if (typeof object.row === "number" && typeof object.column === "number") {
    return { row: object.row, column: object.column };
  }

  return null;
}

function getParentObjectId(objectId?: string): string | undefined {
  if (!objectId) {
    return undefined;
  }

  const underscoreIndex = objectId.indexOf("_");

  return underscoreIndex === -1 ? undefined : objectId.slice(0, underscoreIndex);
}

function isNonNegativeInteger(s: string): boolean {
  if (!s) return false;
  const n = Number(s);
  return Number.isInteger(n) && n >= 0 && String(n) === s;
}

function normalizeTableGroupingName(tableName?: string): string | undefined {
  const trimmed = tableName?.trim();

  if (!trimmed) {
    return undefined;
  }

  const closeIndex = trimmed.lastIndexOf(")");
  if (closeIndex !== trimmed.length - 1) return trimmed;

  const openIndex = trimmed.lastIndexOf("(", closeIndex);
  if (openIndex === -1) return trimmed;

  const inner = trimmed.slice(openIndex + 1, closeIndex).trim();
  const parts = inner.split(",");
  if (parts.length === 2 && parts.every((p) => isNonNegativeInteger(p.trim()))) {
    return trimmed.slice(0, openIndex).trim() || trimmed;
  }

  return trimmed;
}

function getReferencedTagIds(rule: Pick<Rule, "script" | "tagIds">): string[] {
  const referencedTagIds = new Set((rule.tagIds ?? []).map((tagId) => tagId.trim()).filter(Boolean));
  const ruleScript = rule.script ?? "";

  if (ruleScript.trim()) {
    tokenizeScriptForRendering(ruleScript)
      .filter((token): token is TagScriptToken => token.type === "TAG")
      .forEach((token) => referencedTagIds.add(token.tagId));
  }

  return Array.from(referencedTagIds);
}

function buildRuleDraft(
  rule: Rule,
  index: number,
  fabIdToCanonicalId: Map<string, string> = new Map()
): AskAiReviewRuleDraft {
  if (rule.type === RuleType.CONSISTENCY) {
    const effectiveTagIds = getReferencedTagIds(rule).map((id) => fabIdToCanonicalId.get(id) ?? id);
    return {
      ...rule,
      draftKey: `review-rule-${index}-${rule.id || "new"}`,
      tagIds: effectiveTagIds,
      script: "",
      origin: "new",
    };
  }

  let resolvedScript = rule.script ?? "";
  fabIdToCanonicalId.forEach((canonicalId, fabId) => {
    resolvedScript = resolvedScript.split(`{{${fabId}}}`).join(`{{${canonicalId}}}`);
  });

  return {
    ...rule,
    draftKey: `review-rule-${index}-${rule.id || "new"}`,
    script: resolvedScript,
    origin: "new",
  };
}

function enrichObjectSummary(
  object: CoObjectSummary,
  coObjectsByContentControlId: Map<number, CoObjectItem>
): CoObjectSummary {
  const matchedByContentControlId =
    object.contentControlId !== undefined ? coObjectsByContentControlId.get(object.contentControlId) : undefined;

  if (!matchedByContentControlId) {
    return object;
  }

  const isTableMatch = matchedByContentControlId.info.objectType === CoObjectTypeEnum.TABLE;
  const hasRowAndColumn = typeof object.row === "number" && typeof object.column === "number";

  if (isTableMatch && hasRowAndColumn) {
    return {
      ...object,
      id: `${matchedByContentControlId.id}_${object.row}-${object.column}`,
      objectType: CoObjectTypeEnum.TABLE_CELL,
      text: renderCellTitle({ row: object.row, column: object.column }, matchedByContentControlId.info.objectName),
      documentId: object.documentId ?? matchedByContentControlId.documentId,
      contentControlId: object.contentControlId ?? matchedByContentControlId.contentControlId,
      objectName: object.objectName ?? matchedByContentControlId.info.objectName,
    };
  }

  return {
    ...object,
    id: getObjectIdAsString(object.id) ?? matchedByContentControlId.id ?? object.id,
    objectType: object.objectType ?? matchedByContentControlId.info.objectType,
    documentId: object.documentId ?? matchedByContentControlId.documentId,
    contentControlId: object.contentControlId ?? matchedByContentControlId.contentControlId,
    objectName: object.objectName ?? matchedByContentControlId.info.objectName,
  };
}

function buildObjectIdentitySet(
  objects: CoObjectSummary[],
  coObjectsByContentControlId: Map<number, CoObjectItem>
): Set<string> {
  return new Set(
    objects
      .filter((object): object is CoObjectSummary => Boolean(object))
      .map((object) => enrichObjectSummary(object, coObjectsByContentControlId))
      .map((object) => getObjectIdAsString(object.id))
      .filter((objectId): objectId is string => Boolean(objectId))
  );
}

function removeDuplicateExistingObjects(
  objectDrafts: CoObjectSummary[],
  existingTag: Tag | undefined,
  coObjectsByContentControlId: Map<number, CoObjectItem>
): CoObjectSummary[] {
  if (!existingTag) {
    return objectDrafts;
  }

  const existingObjectIds = buildObjectIdentitySet(existingTag.objects, coObjectsByContentControlId);
  const newObjectDrafts = objectDrafts.filter((object) => {
    const objectId = getObjectIdAsString(object.id);
    return !objectId || !existingObjectIds.has(objectId);
  });

  return newObjectDrafts.length > 0 ? newObjectDrafts : objectDrafts;
}

function findTagCaseInsensitive(availableTagsById: Map<string, Tag>, id: string): Tag | undefined {
  const lowerTarget = id.toLowerCase();
  for (const [key, value] of availableTagsById) {
    if (key.toLowerCase() === lowerTarget) return value;
  }
  return undefined;
}

function buildTagDraft(
  tag: Tag,
  index: number,
  availableTagsById: Map<string, Tag>,
  coObjectsByContentControlId: Map<number, CoObjectItem>
): AskAiReviewTagDraft {
  // Match by ID (exact → case-insensitive) then fallback to exact label match.
  // Label fallback handles cases where FAB uses a non-matching ID format.
  const existingTag =
    (tag.id ? (availableTagsById.get(tag.id) ?? findTagCaseInsensitive(availableTagsById, tag.id)) : undefined) ??
    (tag.label ? [...availableTagsById.values()].find((t) => t.label === tag.label) : undefined);
  const canonicalTagId = existingTag ? existingTag.id : `${REVIEW_TAG_ID_PREFIX}-${index}`;
  const objectDrafts = (Array.isArray(tag.objects) ? tag.objects : [])
    .filter((object): object is CoObjectSummary => Boolean(object))
    .map((object) => enrichObjectSummary(object, coObjectsByContentControlId));
  const displayObjectDrafts = removeDuplicateExistingObjects(objectDrafts, existingTag, coObjectsByContentControlId);

  return {
    ...tag,
    id: canonicalTagId,
    label: tag.label,
    draftKey: `review-tag-${index}-${canonicalTagId}`,
    objects: displayObjectDrafts,
    selectedObjectIds: displayObjectDrafts.map((object) => object.id),
    origin: "new",
  };
}

function buildTagObjectForReview(objectDraft: AskAiReviewObjectDraft): CoObjectSummary {
  return {
    id: objectDraft.id,
    text: objectDraft.text,
    documentId: objectDraft.documentId,
    contentControlId: objectDraft.contentControlId,
    objectName: objectDraft.objectName,
    objectType: objectDraft.objectType,
    formatMetadata: objectDraft.formatMetadata,
    row: objectDraft.row,
    column: objectDraft.column,
  };
}

function buildTagForReview(tagDraft: AskAiReviewTagDraft): Tag {
  return {
    id: tagDraft.id ?? tagDraft.draftKey,
    label: tagDraft.label,
    isDeleted: tagDraft.isDeleted,
    objects: tagDraft.objects
      .filter((object) => tagDraft.selectedObjectIds.includes(object.id))
      .map(buildTagObjectForReview),
  };
}

function buildRuleForReview(ruleDraft: AskAiReviewRuleDraft): Rule {
  return {
    id: ruleDraft.id,
    type: ruleDraft.type,
    title: ruleDraft.title,
    version: ruleDraft.version,
    script: ruleDraft.script,
    tagIds: ruleDraft.tagIds,
  };
}

function mergeReferenceTags(availableTags: Tag[], reviewTags: Tag[]): Tag[] {
  const mergedTags = new Map<string, Tag>();

  availableTags.forEach((tag) => {
    mergedTags.set(tag.id, tag);
  });

  reviewTags.forEach((tag) => {
    mergedTags.set(tag.id, tag);
  });

  return Array.from(mergedTags.values());
}

function updateTagSelection(tag: AskAiReviewTagDraft, objectId: string, selected: boolean): AskAiReviewTagDraft {
  if (selected && !tag.selectedObjectIds.includes(objectId)) {
    return {
      ...tag,
      selectedObjectIds: [...tag.selectedObjectIds, objectId],
    };
  }

  if (!selected) {
    return {
      ...tag,
      selectedObjectIds: tag.selectedObjectIds.filter((currentObjectId) => currentObjectId !== objectId),
    };
  }

  return tag;
}

function isDeletedTagTableGroup(expandedGroupId: string, deletedTagDraftKeys: Set<string>): boolean {
  return Array.from(deletedTagDraftKeys).some((deletedTagDraftKey) =>
    expandedGroupId.startsWith(`${deletedTagDraftKey}-table-`)
  );
}

function getNextExpandedRuleIds(
  expandedRuleIds: string[],
  deletedRuleDraftKey: string,
  remainingRules: AskAiReviewRuleDraft[]
): string[] {
  const nextExpandedRuleIds = expandedRuleIds.filter((expandedRuleId) => expandedRuleId !== deletedRuleDraftKey);

  if (nextExpandedRuleIds.length > 0 || remainingRules.length === 0) {
    return nextExpandedRuleIds;
  }

  return [remainingRules[0].draftKey];
}

function filterExpandedTagObjectIds(expandedTagObjectIds: string[], deletedTagDraftKeys: Set<string>): string[] {
  return expandedTagObjectIds.filter((expandedTagId) => !deletedTagDraftKeys.has(expandedTagId));
}

function filterExpandedTableGroupIds(expandedTableGroupIds: string[], deletedTagDraftKeys: Set<string>): string[] {
  return expandedTableGroupIds.filter(
    (expandedGroupId) => !isDeletedTagTableGroup(expandedGroupId, deletedTagDraftKeys)
  );
}

function coObjectSummaryShallowEqual(a: CoObjectSummary, b: CoObjectSummary): boolean {
  return a.id === b.id && a.contentControlId === b.contentControlId && a.objectType === b.objectType;
}

function coObjectArraysEqual(a: CoObjectSummary[], b: CoObjectSummary[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((obj, i) => coObjectSummaryShallowEqual(obj, b[i]));
}

function stringArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

function buildInitialReviewState(rules: Rule[], tags: Tag[], availableTags: Tag[], coObjects: CoObjectItem[]) {
  const availableTagsById = new Map(availableTags.map((tag) => [tag.id, tag]));

  const coObjectsByContentControlId = new Map(
    coObjects.filter((o) => o.contentControlId !== undefined).map((o) => [o.contentControlId, o])
  );

  const tagDrafts = tags.map((tag, index) => buildTagDraft(tag, index, availableTagsById, coObjectsByContentControlId));

  // Build mapping from original FAB tag IDs to canonical draft IDs so that
  // rule scripts and tagIds referencing FAB IDs are remapped before display.
  const fabIdToCanonicalId = new Map<string, string>();
  tags.forEach((tag, index) => {
    const canonicalId = tagDrafts[index].id;
    if (tag.id && tag.id !== canonicalId) {
      fabIdToCanonicalId.set(tag.id, canonicalId);
    }
  });

  const ruleDrafts = rules.map((rule, index) => buildRuleDraft(rule, index, fabIdToCanonicalId));

  return { ruleDrafts, tagDrafts };
}

export function useAskAiReviewSaveModal(props: AskAiReviewSaveModalProps): AskAiReviewSaveModalResult {
  const { documentId, coObjects = [], existingRules = [], availableTags = [], onHide, onSave } = props;
  const dispatch = useAppDispatch();
  const stagedRules = useAppSelector((state) => state.askAiChat.stagedRules);
  const stagedTags = useAppSelector((state) => state.askAiChat.stagedTags);
  const stagedTagEdits = useAppSelector((state) => state.askAiChat.stagedTagEdits);
  const stagedRuleEdits = useAppSelector((state) => state.askAiChat.stagedRuleEdits);
  const initialReviewState = useMemo(
    () => buildInitialReviewState(stagedRules, stagedTags, availableTags, coObjects),
    [availableTags, coObjects, stagedRules, stagedTags]
  );
  const [proposedRuleDrafts, setProposedRuleDrafts] = useState<AskAiReviewRuleDraft[]>(
    () => initialReviewState.ruleDrafts
  );
  const [proposedTagDrafts, setProposedTagDrafts] = useState<AskAiReviewTagDraft[]>(() => initialReviewState.tagDrafts);
  const [expandedRuleIds, setExpandedRuleIds] = useState<string[]>(() =>
    initialReviewState.ruleDrafts.length > 0 ? [initialReviewState.ruleDrafts[0].draftKey] : []
  );
  const [expandedTagSectionIds, setExpandedTagSectionIds] = useState<string[]>([]);
  const [expandedTagObjectIds, setExpandedTagObjectIds] = useState<string[]>([]);
  const [expandedTableGroupIds, setExpandedTableGroupIds] = useState<string[]>([]);

  const [tagEditDrafts, setTagEditDrafts] = useState<Record<string, Tag>>(() =>
    Object.fromEntries(stagedTagEdits.map((e) => [e.targetId, { ...e.patch }]))
  );
  const [tagEditDraftSelectedObjectIds, setTagEditDraftSelectedObjectIds] = useState<Record<string, string[]>>(() => {
    // IDS-5945: seed from diff-space IDs only so rawSelectedIds never contains pre-existing object IDs
    const availableTagsMap = new Map(availableTags.map((t) => [t.id, t]));
    return Object.fromEntries(
      stagedTagEdits.map((e) => {
        const existingTag = availableTagsMap.get(e.targetId);
        const patchObjects = e.patch.objects ?? [];
        const diffObjects = removeDuplicateExistingObjects(patchObjects, existingTag, new Map());
        return [e.targetId, diffObjects.map((o) => String(o.id ?? ""))];
      })
    );
  });
  const [ruleEditDrafts, setRuleEditDrafts] = useState<Record<string, Rule>>(() =>
    Object.fromEntries(stagedRuleEdits.map((e) => [e.targetId, { ...e.patch }]))
  );

  useEffect(() => {
    setProposedRuleDrafts(initialReviewState.ruleDrafts);
    setProposedTagDrafts(initialReviewState.tagDrafts);
    setExpandedRuleIds(initialReviewState.ruleDrafts.length > 0 ? [initialReviewState.ruleDrafts[0].draftKey] : []);
    setExpandedTagSectionIds([]);
    setExpandedTagObjectIds([]);
    setExpandedTableGroupIds([]);
  }, [initialReviewState]);

  useEffect(() => {
    if (props.isShown) {
      setTagEditDrafts(Object.fromEntries(stagedTagEdits.map((e) => [e.targetId, { ...e.patch }])));
      // IDS-5945: seed from diff-space IDs only so rawSelectedIds never contains pre-existing object IDs
      const availableTagsMapForReseed = new Map(availableTags.map((t) => [t.id, t]));
      setTagEditDraftSelectedObjectIds(
        Object.fromEntries(
          stagedTagEdits.map((e) => {
            const existingTag = availableTagsMapForReseed.get(e.targetId);
            const patchObjects = e.patch.objects ?? [];
            const diffObjects = removeDuplicateExistingObjects(patchObjects, existingTag, new Map());
            return [e.targetId, diffObjects.map((o) => String(o.id ?? ""))];
          })
        )
      );
      setRuleEditDrafts(Object.fromEntries(stagedRuleEdits.map((e) => [e.targetId, { ...e.patch }])));
    }
  }, [props.isShown, stagedTagEdits, stagedRuleEdits, availableTags]);

  const existingRuleIds = useMemo(() => new Set(existingRules.map((rule) => rule.id)), [existingRules]);
  const availableTagIds = useMemo(() => new Set(availableTags.map((tag) => tag.id)), [availableTags]);
  const coObjectsById = useMemo(
    () => new Map(coObjects.filter((object) => Boolean(object.id)).map((object) => [object.id, object])),
    [coObjects]
  );
  const coObjectsByContentControlId = useMemo(
    () =>
      new Map(
        coObjects
          .filter((object) => object.contentControlId !== undefined)
          .map((object) => [object.contentControlId, object])
      ),
    [coObjects]
  );

  const outdatedTagEditIds = useMemo(
    () =>
      stagedTagEdits
        .filter((edit) => {
          const committedTag = availableTags.find((t) => t.id === edit.targetId);
          if (!committedTag || committedTag.isDeleted === true) return true;
          if (committedTag.label !== edit.capturedVersion.label) return true;
          return !coObjectArraysEqual(committedTag.objects, edit.capturedVersion.objects);
        })
        .map((edit) => edit.targetId),
    [stagedTagEdits, availableTags]
  );

  const outdatedRuleEditIds = useMemo(
    () =>
      stagedRuleEdits
        .filter((edit) => {
          const committedRule = existingRules.find((r) => r.id === edit.targetId);
          if (!committedRule) return true;
          if (committedRule.title !== edit.capturedVersion.title) return true;
          if (!stringArraysEqual(committedRule.tagIds ?? [], edit.capturedVersion.tagIds ?? [])) return true;
          return committedRule.script !== edit.capturedVersion.script;
        })
        .map((edit) => edit.targetId),
    [stagedRuleEdits, existingRules]
  );

  const availableTagsById = useMemo(() => new Map(availableTags.map((tag) => [tag.id, tag])), [availableTags]);

  // Unified tag drafts: proposed-new rows first, then edit rows.
  // For edit rows, hide objects that are already present on the committed tag so the row shows
  // only the actual diff. If every patch object is already present, fall back to the full list
  // to avoid rendering an empty row.
  const tagDrafts = useMemo<AskAiReviewTagDraft[]>(
    () => [
      ...proposedTagDrafts,
      ...Object.entries(tagEditDrafts).map(([targetId, patch]) => {
        const existingTag = availableTagsById.get(targetId);
        const patchObjects = (patch.objects ?? []) as AskAiReviewObjectDraft[];
        const displayObjects = removeDuplicateExistingObjects(
          patchObjects,
          existingTag,
          coObjectsByContentControlId
        ) as AskAiReviewObjectDraft[];
        const displayObjectIds = new Set(displayObjects.map((o) => String(o.id ?? "")));
        const rawSelectedIds = tagEditDraftSelectedObjectIds[targetId] ?? patchObjects.map((o) => String(o.id ?? ""));
        const selectedObjectIds = rawSelectedIds.filter((id) => displayObjectIds.has(id));
        return {
          ...patch,
          id: targetId,
          label: patch.label ?? "",
          objects: displayObjects,
          // IDS-5945: Edit A seeds only diff-space IDs, so an empty selection means the user
          // explicitly deselected all — pass through without re-seeding.
          selectedObjectIds: selectedObjectIds,
          draftKey: `${EDIT_TAG_DRAFT_PREFIX}${targetId}`,
          origin: "edit" as const,
          targetId,
        };
      }),
    ],
    [proposedTagDrafts, tagEditDrafts, tagEditDraftSelectedObjectIds, availableTagsById, coObjectsByContentControlId]
  );

  // Unified rule drafts: proposed-new rows first, then edit rows.
  const ruleDrafts = useMemo<AskAiReviewRuleDraft[]>(
    () => [
      ...proposedRuleDrafts,
      ...Object.entries(ruleEditDrafts).map(([targetId, patch]) => ({
        ...patch,
        draftKey: `${EDIT_RULE_DRAFT_PREFIX}${targetId}`,
        origin: "edit" as const,
        targetId,
      })),
    ],
    [proposedRuleDrafts, ruleEditDrafts]
  );

  const reviewTags = useMemo(() => proposedTagDrafts.map(buildTagForReview), [proposedTagDrafts]);

  const referenceTags = useMemo(() => mergeReferenceTags(availableTags, reviewTags), [availableTags, reviewTags]);

  const handleRulesAccordionToggle = useCallback((_event: unknown, data: { openItems?: readonly unknown[] }) => {
    setExpandedRuleIds(normalizeOpenItems(data));
  }, []);

  const handleTagsAccordionToggle = useCallback((_event: unknown, data: { openItems?: readonly unknown[] }) => {
    setExpandedTagSectionIds(normalizeOpenItems(data));
  }, []);

  const handleTagObjectsAccordionToggle = useCallback((_event: unknown, data: { openItems?: readonly unknown[] }) => {
    setExpandedTagObjectIds(normalizeOpenItems(data));
  }, []);

  const handleTableGroupAccordionToggle = useCallback(
    (groupId: string, _event: unknown, data: { openItems?: readonly unknown[] }) => {
      const isOpen = normalizeOpenItems(data).includes(groupId);
      setExpandedTableGroupIds((currentGroupIds) => toggleInArray(currentGroupIds, groupId, isOpen));
    },
    []
  );

  const handleRuleTitleChange = useCallback((ruleDraftKey: string, nextTitle: string) => {
    if (ruleDraftKey.startsWith(EDIT_RULE_DRAFT_PREFIX)) {
      const targetId = ruleDraftKey.slice(EDIT_RULE_DRAFT_PREFIX.length);
      setRuleEditDrafts((prev) => ({ ...prev, [targetId]: { ...prev[targetId], title: nextTitle } }));
    } else {
      setProposedRuleDrafts((currentRules) =>
        currentRules.map((rule) => (rule.draftKey === ruleDraftKey ? { ...rule, title: nextTitle } : rule))
      );
    }
  }, []);

  const handleRuleScriptChange = useCallback((ruleDraftKey: string, nextScript: string) => {
    if (ruleDraftKey.startsWith(EDIT_RULE_DRAFT_PREFIX)) {
      const targetId = ruleDraftKey.slice(EDIT_RULE_DRAFT_PREFIX.length);
      setRuleEditDrafts((prev) => ({ ...prev, [targetId]: { ...prev[targetId], script: nextScript } }));
    } else {
      setProposedRuleDrafts((currentRules) =>
        currentRules.map((rule) => (rule.draftKey === ruleDraftKey ? { ...rule, script: nextScript } : rule))
      );
    }
  }, []);

  const handleRuleTagIdsChange = useCallback((ruleDraftKey: string, nextTagIds: string[]) => {
    if (ruleDraftKey.startsWith(EDIT_RULE_DRAFT_PREFIX)) {
      const targetId = ruleDraftKey.slice(EDIT_RULE_DRAFT_PREFIX.length);
      setRuleEditDrafts((prev) => ({ ...prev, [targetId]: { ...prev[targetId], tagIds: nextTagIds } }));
    } else {
      setProposedRuleDrafts((currentRules) =>
        currentRules.map((rule) => (rule.draftKey === ruleDraftKey ? { ...rule, tagIds: nextTagIds } : rule))
      );
    }
  }, []);

  const handleRuleDelete = useCallback(
    (ruleDraftKey: string) => {
      if (ruleDraftKey.startsWith(EDIT_RULE_DRAFT_PREFIX)) {
        // Edit rule: remove from Redux and local state, clean up expand list.
        const targetId = ruleDraftKey.slice(EDIT_RULE_DRAFT_PREFIX.length);
        dispatch(removeStagedRuleEdit(targetId));
        setRuleEditDrafts((prev) => {
          const next = { ...prev };
          delete next[targetId];
          return next;
        });
        setExpandedRuleIds((curr) => curr.filter((id) => id !== ruleDraftKey));
        return;
      }

      // New rule: cascade tag deletions in proposedTagDrafts.
      const deletedRule = proposedRuleDrafts.find((rule) => rule.draftKey === ruleDraftKey);

      if (!deletedRule) {
        return;
      }

      const remainingProposedRules = proposedRuleDrafts.filter((rule) => rule.draftKey !== ruleDraftKey);
      const deletedRuleTagIds = new Set(getReferencedTagIds(deletedRule));
      const remainingRuleTagIds = new Set(remainingProposedRules.flatMap(getReferencedTagIds));
      const deletedTagDrafts = proposedTagDrafts.filter(
        (tag) => deletedRuleTagIds.has(tag.id) && !remainingRuleTagIds.has(tag.id) && !availableTagIds.has(tag.id)
      );
      const deletedTagIds = new Set(deletedTagDrafts.map((tag) => tag.id));
      const deletedTagDraftKeys = new Set(deletedTagDrafts.map((tag) => tag.draftKey));

      setProposedRuleDrafts(remainingProposedRules);
      setProposedTagDrafts(proposedTagDrafts.filter((tag) => !deletedTagIds.has(tag.id)));
      // For expandedRuleIds auto-expansion, compute unified remaining rules (edit rules unchanged).
      const remainingEditRuleItems = Object.keys(ruleEditDrafts).map(
        (tId) => ({ draftKey: `${EDIT_RULE_DRAFT_PREFIX}${tId}` }) as AskAiReviewRuleDraft
      );
      setExpandedRuleIds(
        getNextExpandedRuleIds(expandedRuleIds, ruleDraftKey, [...remainingProposedRules, ...remainingEditRuleItems])
      );
      setExpandedTagObjectIds(filterExpandedTagObjectIds(expandedTagObjectIds, deletedTagDraftKeys));
      setExpandedTableGroupIds(filterExpandedTableGroupIds(expandedTableGroupIds, deletedTagDraftKeys));
    },
    [
      availableTagIds,
      dispatch,
      expandedRuleIds,
      expandedTableGroupIds,
      expandedTagObjectIds,
      proposedRuleDrafts,
      proposedTagDrafts,
      ruleEditDrafts,
    ]
  );

  const handleTagLabelChange = useCallback((tagDraftKey: string, nextLabel: string) => {
    if (tagDraftKey.startsWith(EDIT_TAG_DRAFT_PREFIX)) {
      const targetId = tagDraftKey.slice(EDIT_TAG_DRAFT_PREFIX.length);
      setTagEditDrafts((prev) => ({ ...prev, [targetId]: { ...prev[targetId], label: nextLabel } }));
    } else {
      setProposedTagDrafts((currentTags) =>
        currentTags.map((tag) => (tag.draftKey === tagDraftKey ? { ...tag, label: nextLabel } : tag))
      );
    }
  }, []);

  const handleTagObjectToggle = useCallback((tagDraftKey: string, objectId: string, selected = false) => {
    if (tagDraftKey.startsWith(EDIT_TAG_DRAFT_PREFIX)) {
      const targetId = tagDraftKey.slice(EDIT_TAG_DRAFT_PREFIX.length);
      setTagEditDraftSelectedObjectIds((prev) => {
        const current = prev[targetId] ?? [];
        const next = selected
          ? current.includes(objectId)
            ? current
            : [...current, objectId]
          : current.filter((id) => id !== objectId);
        return { ...prev, [targetId]: next };
      });
    } else {
      setProposedTagDrafts((currentTags) =>
        currentTags.map((tag) => {
          if (tag.draftKey !== tagDraftKey) {
            return tag;
          }

          return updateTagSelection(tag, objectId, selected);
        })
      );
    }
  }, []);

  const handleObjectClick = useCallback((object: CoObjectSummary) => {
    void selectCellOrContentControl(object);
  }, []);

  const updateTagEditDraft = useCallback((targetId: string, patch: Partial<Tag>) => {
    setTagEditDrafts((prev) => ({ ...prev, [targetId]: { ...prev[targetId], ...patch } }));
  }, []);

  const removeTagEditDraft = useCallback(
    (targetId: string) => {
      dispatch(removeStagedTagEdit(targetId));
      setTagEditDrafts((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
    },
    [dispatch]
  );

  const updateRuleEditDraft = useCallback((targetId: string, patch: Partial<Rule>) => {
    setRuleEditDrafts((prev) => ({ ...prev, [targetId]: { ...prev[targetId], ...patch } }));
  }, []);

  const removeRuleEditDraft = useCallback(
    (targetId: string) => {
      dispatch(removeStagedRuleEdit(targetId));
      setRuleEditDrafts((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
    },
    [dispatch]
  );

  const discardOutdatedTagEdit = useCallback(
    (targetId: string) => {
      dispatch(removeStagedTagEdit(targetId));
      setTagEditDrafts((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
      setTagEditDraftSelectedObjectIds((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
    },
    [dispatch]
  );

  const discardOutdatedRuleEdit = useCallback(
    (targetId: string) => {
      dispatch(removeStagedRuleEdit(targetId));
      setRuleEditDrafts((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
    },
    [dispatch]
  );

  const hasPendingEdits = Object.keys(tagEditDrafts).length + Object.keys(ruleEditDrafts).length > 0;

  const getRuleDraftErrors = useCallback(
    (ruleDraftKey: string): RuleDraftErrors => {
      const draft = ruleDrafts.find((r) => r.draftKey === ruleDraftKey);
      if (!draft) return { titleError: "", scriptError: "", consistencyError: "" };

      // Outdated edit rows surface as a row-level blocking error.
      if (draft.origin === "edit" && draft.targetId && outdatedRuleEditIds.includes(draft.targetId)) {
        return {
          titleError: formatMessageDefault(CAPTION_REVIEW_MODAL_OUTDATED_RULE_EDIT, "Rule has been modified"),
          scriptError: "",
          consistencyError: "",
        };
      }

      const trimmedTitle = draft.title.trim();
      let titleError = "";
      if (!trimmedTitle) {
        titleError = formatMessageDefault("cof.validation.info.emptyrulename", "Rule name is required");
      } else {
        const duplicateInExisting = existingRules.some(
          (r) => r.title.toLowerCase() === trimmedTitle.toLowerCase() && r.id !== draft.id
        );
        const duplicateInDrafts = ruleDrafts.some(
          (r) => r.draftKey !== ruleDraftKey && r.title.trim().toLowerCase() === trimmedTitle.toLowerCase()
        );
        if (duplicateInExisting || duplicateInDrafts) {
          titleError = formatMessageDefault(
            "cof.validation.info.uniquerulename",
            "A rule with this name already exists"
          );
        }
      }

      const scriptError = draft.type === RuleType.CUSTOM ? (validateRule(draft.script) ?? "") : "";

      let consistencyError = "";
      if (draft.type === RuleType.CONSISTENCY) {
        const allKnownTagIds = new Set([
          ...tagDrafts.filter((t) => !t.isDeleted).map((t) => t.id),
          ...Array.from(availableTagIds),
        ]);
        const selectedCount = (draft.tagIds ?? []).filter((id) => allKnownTagIds.has(id)).length;
        if (selectedCount < 1) {
          consistencyError = formatMessageDefault(
            "cof.validation.info.consistencyrequirestags",
            "Select at least one tag"
          );
        }
      }

      return { titleError, scriptError, consistencyError };
    },
    [ruleDrafts, existingRules, tagDrafts, availableTagIds, outdatedRuleEditIds]
  );

  const getTagDraftErrors = useCallback(
    (tagDraftKey: string): TagDraftErrors => {
      const tag = tagDrafts.find((t) => t.draftKey === tagDraftKey);
      if (!tag || tag.isDeleted) return { labelError: "", objectsError: "" };

      // Outdated edit rows surface as a row-level blocking error.
      if (tag.origin === "edit" && tag.targetId && outdatedTagEditIds.includes(tag.targetId)) {
        return {
          labelError: formatMessageDefault(CAPTION_REVIEW_MODAL_OUTDATED_TAG_EDIT, "Tag has been modified"),
          objectsError: "",
        };
      }

      const trimmedLabel = tag.label.trim();
      let labelError = "";
      if (!trimmedLabel) {
        labelError = formatMessageDefault("cof.validation.info.emptytagname", "Tag name is required");
      }

      const objectsError =
        !availableTagIds.has(tag.id) && tag.selectedObjectIds.length < 1
          ? formatMessageDefault("cof.validation.info.tagrequiresobjects", "Select at least one object")
          : "";

      return { labelError, objectsError };
    },
    [tagDrafts, availableTagIds, outdatedTagEditIds]
  );

  const isSaveEnabled = useMemo(() => {
    const hasSavableContent = ruleDrafts.length > 0 || tagDrafts.some((tag) => !tag.isDeleted);

    const rulesValid = ruleDrafts.every((draft) => {
      const { titleError, scriptError, consistencyError } = getRuleDraftErrors(draft.draftKey);
      return !titleError && !scriptError && !consistencyError;
    });

    const tagsValid = tagDrafts
      .filter((t) => !t.isDeleted)
      .every((tag) => {
        const { labelError, objectsError } = getTagDraftErrors(tag.draftKey);
        return !labelError && !objectsError;
      });

    return hasSavableContent && rulesValid && tagsValid;
  }, [ruleDrafts, tagDrafts, getRuleDraftErrors, getTagDraftErrors]);

  const handleCancel = useCallback(() => {
    onHide();
  }, [onHide]);

  const handleSave = useCallback(() => {
    // Sync tagEditDrafts back to Redux — capturedVersion MUST come from existing slice entry (immutability invariant).
    Object.keys(tagEditDrafts).forEach((targetId) => {
      const existing = stagedTagEdits.find((e) => e.targetId === targetId);
      if (existing) {
        const rawDraft = tagEditDrafts[targetId];
        const userSelectedIds = new Set(tagEditDraftSelectedObjectIds[targetId] ?? []);
        const existingTag = availableTagsById.get(targetId);
        // IDS-5945: keep pre-existing objects plus user-selected proposed objects; never drop committed data
        const existingObjectIds = buildObjectIdentitySet(existingTag?.objects ?? [], coObjectsByContentControlId);
        const filteredObjects = (rawDraft.objects ?? []).filter(
          (o) => existingObjectIds.has(String(o.id ?? "")) || userSelectedIds.has(String(o.id ?? ""))
        );
        dispatch(
          upsertStagedTagEdit({
            targetId,
            patch: { ...rawDraft, objects: filteredObjects },
            capturedVersion: existing.capturedVersion,
          })
        );
      }
    });
    // Sync ruleEditDrafts back to Redux.
    Object.keys(ruleEditDrafts).forEach((targetId) => {
      const existing = stagedRuleEdits.find((e) => e.targetId === targetId);
      if (existing) {
        dispatch(
          upsertStagedRuleEdit({ targetId, patch: ruleEditDrafts[targetId], capturedVersion: existing.capturedVersion })
        );
      }
    });
    onSave({
      rules: proposedRuleDrafts.map(buildRuleForReview),
      tags: reviewTags.filter((tag) => tag.isDeleted || tag.objects.length > 0 || availableTagIds.has(tag.id)),
    });
  }, [
    availableTagIds,
    availableTagsById,
    coObjectsByContentControlId,
    dispatch,
    onSave,
    reviewTags,
    proposedRuleDrafts,
    ruleEditDrafts,
    stagedRuleEdits,
    stagedTagEdits,
    tagEditDraftSelectedObjectIds,
    tagEditDrafts,
  ]);

  const getRuleStatusLabel = useCallback(
    (ruleId: string) =>
      existingRuleIds.has(ruleId)
        ? formatMessageDefault("cof.validation.title.ruleupdated", "Rule updated")
        : formatMessageDefault("cof.validation.title.rulecreated", "Rule created"),
    [existingRuleIds]
  );

  const getTaggedItemsLabel = useCallback(
    (count: number) =>
      formatMessageWithParamsDefault("cof.validation.label.itemstagged", "{count} items tagged", { count }),
    []
  );

  const resolveObjectData = useCallback(
    (object: CoObjectSummary) => {
      const safeObject = (object ?? {}) as CoObjectSummary;
      const objectWithDraftKey = safeObject as CoObjectSummary & {
        draftKey?: string;
        info?: CoObjectItem["info"];
      };
      const objectInfo = objectWithDraftKey.info;
      const objectId = getObjectIdAsString(safeObject.id);
      const cellAddress = parseCellAddress(safeObject);
      const parentObjectId = getParentObjectId(objectId);
      const matchedObjectByParentId = parentObjectId ? coObjectsById.get(parentObjectId) : undefined;
      const matchedObjectByObjectId = objectId ? coObjectsById.get(objectId) : undefined;
      const matchedObjectByContentControlId =
        safeObject.contentControlId !== undefined
          ? coObjectsByContentControlId.get(safeObject.contentControlId)
          : undefined;
      const tableObject =
        [matchedObjectByParentId, matchedObjectByObjectId, matchedObjectByContentControlId].find(
          (matchedObject) => matchedObject?.info.objectType === CoObjectTypeEnum.TABLE
        ) ?? undefined;
      const baseObject = matchedObjectByParentId ?? matchedObjectByObjectId ?? matchedObjectByContentControlId;

      return {
        safeObject,
        objectWithDraftKey,
        objectInfo,
        objectId,
        cellAddress,
        parentObjectId,
        tableObject,
        baseObject,
      };
    },
    [coObjectsByContentControlId, coObjectsById]
  );

  const getObjectItem = useCallback(
    (object: CoObjectSummary): CoObjectItem => {
      const { safeObject, objectWithDraftKey, objectInfo, objectId, cellAddress, baseObject } =
        resolveObjectData(object);
      const isTableCell = safeObject.objectType === CoObjectTypeEnum.TABLE_CELL || cellAddress !== null;
      const objectType = isTableCell
        ? CoObjectTypeEnum.TABLE_CELL
        : (safeObject.objectType ?? baseObject?.info.objectType ?? objectInfo?.objectType ?? CoObjectTypeEnum.AMOUNT);
      let objectName =
        safeObject.objectName ??
        baseObject?.info.objectName ??
        objectInfo?.objectName ??
        safeObject.text ??
        objectId ??
        objectWithDraftKey.draftKey ??
        "";

      if (objectType === CoObjectTypeEnum.TABLE_CELL && cellAddress) {
        objectName = renderCellTitle(cellAddress, undefined, true);
      }

      return {
        documentId: safeObject.documentId ?? baseObject?.documentId ?? documentId ?? "",
        contentControlId: safeObject.contentControlId ?? baseObject?.contentControlId,
        id: objectId ?? objectWithDraftKey.draftKey,
        info: {
          objectName,
          objectType,
          info: "",
        },
      };
    },
    [documentId, resolveObjectData]
  );

  const getTableObjectItem = useCallback(
    (object: CoObjectSummary): CoObjectItem => {
      const { safeObject, objectWithDraftKey, objectInfo, parentObjectId, tableObject, baseObject } =
        resolveObjectData(object);
      const resolvedDocumentId =
        safeObject.documentId ?? tableObject?.documentId ?? baseObject?.documentId ?? documentId ?? "";
      const resolvedTableName =
        tableObject?.info.objectName ??
        normalizeTableGroupingName(safeObject.objectName) ??
        normalizeTableGroupingName(objectInfo?.objectName) ??
        parentObjectId;
      const resolvedContentControlId = tableObject?.contentControlId;
      const tableId =
        tableObject?.id ??
        (resolvedContentControlId !== undefined ? `table-cc-${resolvedContentControlId}` : undefined) ??
        parentObjectId ??
        (resolvedTableName
          ? `table-name-${encodeURIComponent(resolvedDocumentId)}-${encodeURIComponent(
              normalizeTableGroupingName(resolvedTableName) ?? resolvedTableName
            )}`
          : undefined) ??
        (safeObject.contentControlId !== undefined ? `table-source-cc-${safeObject.contentControlId}` : undefined) ??
        objectWithDraftKey.draftKey;
      const tableName = resolvedTableName ?? parentObjectId ?? objectWithDraftKey.draftKey ?? "";

      return {
        documentId: resolvedDocumentId,
        contentControlId: resolvedContentControlId ?? safeObject.contentControlId,
        id: tableId,
        info: {
          objectName: tableName,
          objectType: CoObjectTypeEnum.TABLE,
          info: "",
        },
      };
    },
    [documentId, resolveObjectData]
  );

  const getTagDisplayItems = useCallback(
    (tagDraft: AskAiReviewTagDraft): AskAiReviewTagDisplayItem[] => {
      const displayItems: AskAiReviewTagDisplayItem[] = [];
      const tableGroups = new Map<string, AskAiReviewTagTableDisplayItem>();

      tagDraft.objects.forEach((object) => {
        const objectItem = getObjectItem(object);

        if (objectItem.info.objectType !== CoObjectTypeEnum.TABLE_CELL) {
          displayItems.push({
            kind: "object",
            object,
            objectItem,
          });
          return;
        }

        const tableObjectItem = getTableObjectItem(object);
        const groupId = `${tagDraft.draftKey}-table-${tableObjectItem.id ?? object.id}`;
        const existingGroup = tableGroups.get(groupId);

        if (existingGroup) {
          existingGroup.cells.push({ object, objectItem });
          return;
        }

        const nextGroup: AskAiReviewTagTableDisplayItem = {
          kind: "table",
          groupId,
          tableObjectItem,
          cells: [{ object, objectItem }],
        };

        tableGroups.set(groupId, nextGroup);
        displayItems.push(nextGroup);
      });

      return displayItems;
    },
    [getObjectItem, getTableObjectItem]
  );

  return {
    ruleDrafts,
    tagDrafts,
    reviewTags,
    referenceTags,
    expandedRuleIds,
    expandedTagSectionIds,
    expandedTagObjectIds,
    expandedTableGroupIds,
    handleRulesAccordionToggle,
    handleTagsAccordionToggle,
    handleTagObjectsAccordionToggle,
    handleTableGroupAccordionToggle,
    handleRuleTitleChange,
    handleRuleScriptChange,
    handleRuleTagIdsChange,
    handleRuleDelete,
    handleTagLabelChange,
    handleTagObjectToggle,
    handleObjectClick,
    handleCancel,
    handleSave,
    getRuleStatusLabel,
    getTaggedItemsLabel,
    getRuleDraftErrors,
    getTagDraftErrors,
    isSaveEnabled,
    getObjectItem,
    getTagDisplayItems,
    tagEditDrafts,
    ruleEditDrafts,
    outdatedTagEditIds,
    outdatedRuleEditIds,
    updateTagEditDraft,
    removeTagEditDraft,
    updateRuleEditDraft,
    removeRuleEditDraft,
    discardOutdatedTagEdit,
    discardOutdatedRuleEdit,
    hasPendingEdits,
  };
}

export { TAGS_SECTION_ID, EDIT_TAG_DRAFT_PREFIX, EDIT_RULE_DRAFT_PREFIX };
